import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/about.css';

const FEATURES = [
  {
    id: 'library',
    accent: 'sapphire',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
    title: 'A growing library at your fingertips',
    body: 'Enjoy a wide range of children’s books that can be accessed wherever you are — at home, in the classroom, or on the go.',
  },
  {
    id: 'islamic',
    accent: 'amber',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 14.5A8 8 0 1 1 9.5 4 6.5 6.5 0 0 0 20 14.5z" />
        <path d="M19 5l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
      </svg>
    ),
    title: 'Books rooted in Islamic values',
    body: 'Deenlit offers books that either teach Islamic topics directly or are carefully selected to be compatible with Islamic morals and values.',
  },
  {
    id: 'bilingual',
    accent: 'teal',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 8 h6 M8 4 v4 M5 12 c4 -2 6 4 12 -2" />
        <path d="M14 18 l3 -8 l3 8 M14.6 16 h4.8" />
      </svg>
    ),
    title: 'Learn in Arabic and English',
    body: 'Children can explore books in both Arabic and English, with audio narration and highlighted text to support reading, listening, and language learning.',
  },
  {
    id: 'levels',
    accent: 'violet',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="14" width="4" height="7" rx="1" />
        <rect x="10" y="9" width="4" height="12" rx="1" />
        <rect x="17" y="4" width="4" height="17" rx="1" />
      </svg>
    ),
    title: 'Reading levels to support every child',
    body: 'Books are organised by reading level, helping children find titles that match their ability and build confidence step by step.',
  },
  {
    id: 'quizzes',
    accent: 'coral',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4 v3 h6 V4" />
        <polyline points="9 13 11 15 15 11" />
      </svg>
    ),
    title: 'Quizzes to check understanding',
    body: 'Interactive quizzes help children reflect on what they have read and strengthen their comprehension.',
  },
  {
    id: 'audio',
    accent: 'rose',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
        <path d="M18.5 5.5a9 9 0 0 1 0 13" />
      </svg>
    ),
    title: 'Audio narration with highlighted text',
    body: 'Children can listen while following the words on screen, supporting pronunciation, fluency, and independent reading.',
  },
  {
    id: 'personal',
    accent: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21 s-7 -4 -9 -9 c-2 -4 1 -8 5 -8 c2 0 3 1 4 3 c1 -2 2 -3 4 -3 c4 0 7 4 5 8 c-2 5 -9 9 -9 9 z" />
      </svg>
    ),
    title: 'Personalised reading features',
    body: 'Children can choose their favourite books, save titles to playlists, and personalise their profile, making the reading experience more enjoyable and engaging.',
  },
];

const FAQS = [
  {
    q: 'How many profiles can I register under a single account?',
    a: 'You can create up to 3 profiles under one account.',
  },
  {
    q: 'How much does Deenlit cost, and can I cancel?',
    a: 'Deenlit costs £7.50 per month. You can cancel your subscription at any time. To avoid being charged for the following month, please make sure you cancel before your next billing date.',
  },
  {
    q: 'Do you provide a free trial?',
    a: 'Yes. Once you register your account, you can enjoy a one-week free trial. After the free trial ends, your subscription will automatically continue at £7.50 per month unless you cancel before the trial period ends.',
  },
  {
    q: 'How do I switch to Arabic?',
    a: 'When you select a book, you may have the option to read the same book in Arabic. You can also switch languages after opening the book, where available.',
  },
  {
    q: 'Do you provide different reading formats?',
    a: 'Yes. Books can currently be switched between single-page and two-page formats. We also support both landscape and portrait modes on phones and tablets.',
  },
  {
    q: 'How do I turn the page?',
    a: 'You can turn the page by gently swiping from one side to the other, or by clicking the arrows. On mobile portrait mode, pages can be turned by swiping.',
  },
  {
    q: 'I can’t see some of the text on mobile landscape mode. What should I do?',
    a: 'For the best reading experience, we recommend using a tablet, laptop, or 1 page view on mobile phone in portrait mode. However, in mobile landscape mode, you can use the scroll bar to move down and read the text. You may also be able to adjust the page by gently dragging it with your finger.',
  },
  {
    q: 'Why do you not show faces?',
    a: 'We are mindful of the hadith concerning image-making, so we try our best to avoid showing faces or heads in our illustrations. We believe children’s books can still be beautiful, engaging, and high quality while following this approach.',
  },
];

const STATS = [
  { value: 'EN · AR', label: 'Bilingual' },
  { value: 'Voiced', label: 'Read aloud' },
  { value: 'Ages 3–12', label: 'For every reader' },
  { value: '£7.50/mo', label: '1-week free trial' },
];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.18 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function RevealSection({ className = '', children, ...rest }) {
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} className={`${className} ${visible ? 'is-visible' : ''}`} {...rest}>
      {children}
    </section>
  );
}

export default function About() {
  return (
    <div className="about-wrapper">
      <div className="about-orb about-orb-1" aria-hidden="true" />
      <div className="about-orb about-orb-2" aria-hidden="true" />
      <div className="about-orb about-orb-3" aria-hidden="true" />

      <div className="about-inner">

        {/* ── Hero ── */}
        <header className="about-hero">
          <div className="about-eyebrow">
            <span className="about-ornament">&#10040;</span>
            About Deenlit
            <span className="about-ornament">&#10040;</span>
          </div>
          <h1 className="about-title">
            <span className="about-title-line">A World of Islamic Children’s Books,</span>
            <span className="about-title-line is-accent">at Your Fingertips.</span>
          </h1>
          <p className="about-sub">
            Deenlit is a digital reading platform with a growing library of stories, poems,
            history, non-fiction, and more — all carefully selected to be engaging, beneficial,
            and rooted in Islamic values.
          </p>
          <div className="about-stats">
            {STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                <div className="about-stat">
                  <div className="about-stat-value">{s.value}</div>
                  <div className="about-stat-label">{s.label}</div>
                </div>
                {i < STATS.length - 1 && <div className="about-stat-divider" />}
              </React.Fragment>
            ))}
          </div>
        </header>

        {/* ── Our story ── */}
        <RevealSection className="about-story">
          <div className="about-story-art">
            <div className="about-story-blob" aria-hidden="true" />
            <img src="/images/features/story.jpg" alt="An open Qur’an on a velvet stand under a warm lantern" loading="lazy" />
            <span className="about-story-badge">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <span>Our Story</span>
            </span>
          </div>
          <div className="about-story-copy">
            <h2 className="about-section-title">A safer, more meaningful place to read.</h2>
            <p>
              Deenlit is a digital reading platform that allows children to read a wide range of
              children’s books in one easy place. Our library includes stories, poems, history,
              non-fiction, and more — all carefully selected to be engaging, beneficial, and rooted
              in Islamic values. We also have a dedicated Islamic section where children can
              explore stories from the Qur’an, learn about the Prophets, and discover other
              beneficial Islamic topics.
            </p>
            <p>
              Children today are surrounded by screens, digital entertainment, and content that is
              not always beneficial or suitable for them. We wanted to create a safer and more
              meaningful alternative — a space where reading feels inviting, enjoyable, and easy
              to access. Through Deenlit, children explore a growing library of books that
              encourage curiosity, imagination, good character, faith, knowledge, and confidence.
            </p>
            <p>
              Whether a child is reading on their own, listening along, or discovering something
              new, Deenlit is designed to support them step by step. With carefully selected
              titles, reading levels, quizzes, and child-friendly features, we help parents,
              teachers, and children find books that suit each reader’s ability and interests.
            </p>
          </div>
        </RevealSection>

        {/* ── Why Deenlit ── */}
        <RevealSection className="about-features">
          <div className="about-values-head">
            <div className="about-eyebrow about-eyebrow-small">
              <span className="about-ornament">&#10040;</span>
              Why Deenlit
              <span className="about-ornament">&#10040;</span>
            </div>
            <h2 className="about-section-title about-section-title-lg">
              Designed around the way kids learn to love reading.
            </h2>
          </div>
          <div className="about-features-grid">
            {FEATURES.map((v, i) => (
              <article
                key={v.id}
                className={`about-value a-${v.accent}`}
                style={{ '--reveal-delay': `${0.06 * i}s` }}
              >
                <div className="about-value-icon">{v.icon}</div>
                <h3 className="about-value-title">{v.title}</h3>
                <p className="about-value-body">{v.body}</p>
              </article>
            ))}
          </div>
        </RevealSection>

        {/* ── Mission strip ── */}
        <RevealSection className="about-mission">
          <div className="about-mission-quote">
            <div className="about-mission-mark" aria-hidden="true">&ldquo;</div>
            <p>
              Our mission is simple: to bring <strong>high-quality Islamic children’s literature</strong>
              {' '}closer to every <strong>home</strong>, <strong>classroom</strong>, and young reader.
            </p>
            <div className="about-mission-attrib">
              &mdash; The Deenlit team
            </div>
          </div>
        </RevealSection>

        {/* ── FAQ ── */}
        <RevealSection className="about-faq">
          <div className="about-values-head">
            <div className="about-eyebrow about-eyebrow-small">
              <span className="about-ornament">&#10040;</span>
              Frequently asked
              <span className="about-ornament">&#10040;</span>
            </div>
            <h2 className="about-section-title about-section-title-lg">
              Everything you might want to know.
            </h2>
          </div>
          <div className="about-faq-list">
            {FAQS.map((f, i) => (
              <details key={i} className="about-faq-item">
                <summary>
                  <span className="about-faq-q">{f.q}</span>
                  <span className="about-faq-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <line x1="12" y1="5" x2="12" y2="19" className="about-faq-icon-bar" />
                    </svg>
                  </span>
                </summary>
                <div className="about-faq-a">
                  <p>{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </RevealSection>

        {/* ── CTA banner ── */}
        <RevealSection className="about-cta">
          <h2 className="about-cta-title">Ready to open a book?</h2>
          <p className="about-cta-sub">
            Step inside the library and discover the next bedtime story.
          </p>
          <div className="about-cta-buttons">
            <Link to="/category/all" className="about-cta-btn about-cta-btn-primary">
              Browse the Library
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
            </Link>
            <Link to="/contact" className="about-cta-btn about-cta-btn-ghost">
              Get in touch
            </Link>
          </div>
        </RevealSection>

      </div>
    </div>
  );
}
